import {
  getSafeVideoRatio,
  getSafeVideoResolution,
  VIDEO_CHANNEL_OPTIONS,
} from "~/common/constants"
import { normalizeTemplateValues, type TemplateValues } from "./index"
import { createRandomId } from "~/common/utils"
const oneClickCopyRetryTransferKey = "one-click-copy-retry-transfer"

export type OneClickCopyRetryTransferPayload = {
  duration?: number
  imageUrls: string[]
  audioUrls?: string[]
  videoUrls?: string[]
  model?: API.Task.CreateVideoChannel
  prompt: string
  ratio?: API.VideoRatio
  resolution?: API.VideoResolution
  templateValues: TemplateValues
}

export type OneClickCopyRetryTransferResult =
  | { status: "empty" }
  | { status: "invalid" }
  | {
      payload: OneClickCopyRetryTransferPayload
      status: "ready"
    }

const normalizeRetryTransferPayload = (
  value: unknown,
): OneClickCopyRetryTransferPayload | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null

  const payload = value as Partial<OneClickCopyRetryTransferPayload>
  const templateValues = normalizeTemplateValues(payload.templateValues)

  if (
    !templateValues ||
    typeof payload.prompt !== "string" ||
    !Array.isArray(payload.imageUrls) ||
    !payload.imageUrls.every((url) => typeof url === "string") ||
    (payload.audioUrls !== undefined &&
      (!Array.isArray(payload.audioUrls) ||
        !payload.audioUrls.every((url) => typeof url === "string"))) ||
    (payload.videoUrls !== undefined &&
      (!Array.isArray(payload.videoUrls) ||
        !payload.videoUrls.every((url) => typeof url === "string")))
  ) {
    return null
  }

  return {
    duration:
      typeof payload.duration === "number" && Number.isFinite(payload.duration)
        ? payload.duration
        : undefined,
    imageUrls: payload.imageUrls,
    audioUrls: payload.audioUrls ?? [],
    videoUrls: payload.videoUrls ?? [],
    model: VIDEO_CHANNEL_OPTIONS.some((item) => item.value === payload.model)
      ? payload.model
      : undefined,
    prompt: payload.prompt,
    ratio: payload.model
      ? getSafeVideoRatio(payload.model, payload.ratio)
      : undefined,
    resolution: payload.model
      ? getSafeVideoResolution(payload.model, payload.resolution)
      : undefined,
    templateValues,
  }
}

export const consumeOneClickCopyRetryTransfer =
  (): OneClickCopyRetryTransferResult => {
    try {
      const value = window.localStorage.getItem(oneClickCopyRetryTransferKey)
      if (!value) return { status: "empty" }
      window.localStorage.removeItem(oneClickCopyRetryTransferKey)
      const payload = normalizeRetryTransferPayload(JSON.parse(value))

      return payload ? { payload, status: "ready" } : { status: "invalid" }
    } catch {
      return { status: "invalid" }
    }
  }

const RETRY_TTL = 30 * 60 * 1000
const RETRY_PREFIX = `${oneClickCopyRetryTransferKey}:`

// Tokens isolate drafts opened in different tabs. Shared URLs never include one.
export const saveTemplateRetry = (
  payload: OneClickCopyRetryTransferPayload,
): string => {
  const now = Date.now()
  for (const key of Object.keys(window.localStorage)) {
    if (!key.startsWith(RETRY_PREFIX)) continue
    try {
      const entry = JSON.parse(window.localStorage.getItem(key) || "null") as {
        createdAt?: number
      } | null
      if (!entry?.createdAt || now - entry.createdAt > RETRY_TTL)
        window.localStorage.removeItem(key)
    } catch {
      window.localStorage.removeItem(key)
    }
  }
  const token = createRandomId()
  window.localStorage.setItem(
    `${RETRY_PREFIX}${token}`,
    JSON.stringify({ createdAt: now, payload }),
  )
  return token
}

export const consumeTemplateRetry = (
  token: string,
  templateId: string,
): OneClickCopyRetryTransferResult => {
  if (!/^[a-zA-Z0-9-]{1,80}$/.test(token)) return { status: "invalid" }
  const key = `${RETRY_PREFIX}${token}`
  try {
    const value = window.localStorage.getItem(key)
    if (!value) return { status: "invalid" }
    window.localStorage.removeItem(key)
    const entry = JSON.parse(value) as { createdAt?: number; payload?: unknown }
    if (
      typeof entry.createdAt !== "number" ||
      !Number.isFinite(entry.createdAt) ||
      entry.createdAt > Date.now() ||
      Date.now() - entry.createdAt > RETRY_TTL
    )
      return { status: "invalid" }
    const payload = normalizeRetryTransferPayload(entry.payload)
    return payload && payload.templateValues.templateId === templateId
      ? {
          status: "ready",
          payload,
        }
      : { status: "invalid" }
  } catch {
    return { status: "invalid" }
  }
}
